import { arabicCompanyToEnglish, arabicPlaceToEnglish, hasArabic, westernDigits } from "./arabicText";

/**
 * What a person asks for in plain words when starting or placing a record —
 * read by CODE on this computer, so a new company's or person's name never
 * reaches the AI (D18), and no credit is used. One reader for Ask's typed
 * requests and its replies about an attached list:
 *
 *   "the provider is ABC and based in Oman"
 *   "create new provider ABC, based in Muscat, Oman, operates in Dubai and works in Qatar"
 *   "no, the name is ABC" · "rates are for Qatar" · "both"
 *
 * Each part runs from its cue words to the next part's, so "ABC and based in
 * Oman" is the name ABC and the base Oman. Places stay as written; they're
 * looked up (as the person) when the form is filled.
 */

export type IntentKind = "provider" | "client" | "company" | "contact" | "lead";

export interface RecordIntent {
    kind: IntentKind | null;
    name: string | null;
    /** Where it's based — its address ("Muscat, Oman"). */
    base: string | null;
    /** Where it works — where its services and rates apply ("Dubai", "Qatar"). */
    coverage: string[];
    /** Which of those places a list's rates are for — "all" for every one. */
    ratesFor: string[] | "all" | null;
    /** "… with contact Layla Haddad 050…": a contact to open after it. */
    contact: string | null;
    /** Phones, emails and a website given for the record itself, one per line. */
    details: string;
    /** The person asked for a new record ("create", "add", "new"). */
    asksNew: boolean;
    /** The organisation's name as written in Arabic, when it was (`name` holds it in English). */
    nameArabic?: string | null;
}

type Slot = "name" | "base" | "coverage" | "ratesFor" | "contact" | "reach";

const KINDS = "provider|supplier|vendor|client|customer|company|contact|person|lead";
const KIND_OF: Array<[RegExp, IntentKind]> = [
    [/^(provider|supplier|vendor)$/i, "provider"],
    [/^(client|customer)$/i, "client"],
    [/^company$/i, "company"],
    [/^(contact|person)$/i, "contact"],
    [/^lead$/i, "lead"],
];

/** The words that start each part. */
const CUES: Array<{ slot: Slot; re: RegExp }> = [
    { slot: "base", re: /\b(?:(?:and|also|which|that|who|is|are|it'?s|it|they'?re|they|we'?re|we)\s+){0,3}(?:based|located|headquartered|registered|incorporated|situated|established)\s+(?:in|at|out\s+of)\s+/gi },
    { slot: "base", re: /\b(?:(?:its|their|the|our)\s+)?(?:office|offices|hq|head\s*office|headquarters|main\s+office|address)\s+(?:is\s+|are\s+)?(?:in|at)\s+/gi },
    { slot: "coverage", re: /\b(?:(?:also|and|they|it|we|now|which|that|who|is|are)\s+){0,3}(?:operates?|operating|works?|working|active|available|present)\s+(?:in|across|throughout|within)\s+/gi },
    { slot: "coverage", re: /\b(?:(?:also|and|they|it|we|now|which|that|who)\s+){0,3}(?:covers?|covering|serves?|serving)\s+(?:in\s+|across\s+)?/gi },
    { slot: "coverage", re: /\b(?:(?:has\s+|have\s+)?expanded|expanding)\s+(?:to|into)\s+/gi },
    { slot: "ratesFor", re: /\b(?:the\s+)?(?:rates?|prices?|price\s+list|list|these)\s+(?:are\s+|is\s+)?(?:for|in|apply\s+(?:to|in))\s+/gi },
    { slot: "contact", re: /\b(?:with\s+|and\s+)?(?:a\s+|the\s+)?contact(?:\s+person)?\s+(?:is\s+)?(?=[\p{Lu}\p{Lo}+0])/giu },
    { slot: "name", re: new RegExp(`\\b(?:the\\s+)?(?:${KINDS})(?:'s)?\\s+(?:name\\s+)?(?:is|=|:)\\s+(?:called\\s+|named\\s+)?`, "gi") },
    { slot: "name", re: /\b(?:the\s+|its\s+|their\s+)?name\s+(?:is|=|:)\s+/gi },
    { slot: "name", re: /\b(?:called|named|names?)\s+/gi },
    { slot: "name", re: new RegExp(`\\b(?:create|add|make|register|set\\s+up|start|new|fill|open)\\s+(?:a\\s+|an\\s+|the\\s+)?(?:new\\s+)?(?:${KINDS})\\s+(?!(?:called|named|names?|name|for|with|that|which|who|based|located|in|and|is|from)\\b)`, "gi") },
    { slot: "name", re: /\b(?:it'?s|this\s+is|for|to)\s+(?=["“']?[\p{Lu}\p{Lo}])/gu },
    // "create ABC Holdings" (the kind asked after)
    { slot: "name", re: /\b(?:create|add|make|register|set\s+up|start)\s+(?:a\s+|an\s+)?(?:new\s+)?(?=["“']?[\p{Lu}\p{Lo}])/gu },
    // the same parts in Arabic (2026-10-15): "اسمه …", "مقره في …", "يعمل في …", "الأسعار لـ …", "جهة الاتصال …", "رقمه …"
    { slot: "base", re: /(?:^|\s)و?(?:مقرّها|مقرها|مقرهم|مقرّه|مقره|مركزها|مركزه|عنوانها|عنوانه)(?![\u0621-\u064A])\s*(?:الرئيسي\s*)?(?:في\s+|ب)?/g },
    { slot: "base", re: /(?:^|\s)و?(?:موجودة|موجود|يقع|تقع)(?![\u0621-\u064A])\s+(?:في\s+|ب)/g },
    { slot: "coverage", re: /(?:^|\s)و?(?:يعملون|يعمل|تعمل|يشتغل|تشتغل|متواجدة|متواجد|يغطي|تغطي|يخدم|تخدم)(?![\u0621-\u064A])\s+(?:أيضا\s+|أيضاً\s+|ايضا\s+)?(?:في\s+|ب)?/g },
    { slot: "ratesFor", re: /(?:^|\s)(?:ال)?(?:أسعار|اسعار|الاسعار|الأسعار)\s+(?:ل|لـ|في|تخص)\s*/g },
    { slot: "contact", re: /(?:^|\s)(?:مع\s+)?(?:جهة\s+(?:ال)?اتصال|المسؤول|الشخص\s+المسؤول)\s*(?:هو|هي|:)?\s*/g },
    { slot: "name", re: /(?:^|\s)(?:اسمها|اسمه|باسم|الاسم|اسم\s+(?:الشركة|المزود|العميل))(?![\u0621-\u064A])\s*(?:هو|هي|:)?\s*/g },
    { slot: "reach", re: /(?:^|\s)و?(?:رقمها|رقمه|رقم|هاتفها|هاتفه|هاتف|جوالها|جواله|جوال|موبايل|واتساب|ايميله|إيميله|ايميل|إيميل|بريده|البريد)(?![\u0621-\u064A])\s*(?:هو|:)?\s*/g },
    // phones, emails, a website: they end the part before them (read separately, into details)
    { slot: "reach", re: /\b(?:(?:its|their|the|with|and)\s+){0,2}(?:phone|tel|telephone|mobile|mob|whatsapp|number|email|e-mail|website|web)\b\s*(?:is\s+|:\s*|number\s+)?/gi },
    { slot: "reach", re: /(?:^|[\s,;])(?=(?:\+|00)\d|[\w.+-]+@[\w-]+\.)/g },
];

/** Connectors and action words that end a value ("ABC and attach this price sheet to it"). */
const VALUE_END = /\s+(?:and|then|,)\s+(?:attach|add|put|upload|load|use|fill|open|create|save|send|make)\b[\s\S]*$|\s+(?:with|using)\s+(?:this|these|the|that)\b[\s\S]*$|\s+[—–]\s+[\s\S]*$/i;
const TRAILING = /[\s,;.:!—–-]*(?:\b(?:and|but|also|then|plus|while|whereas|so|which|that)\b[\s,;.:!—–-]*)*$/i;
const PRONOUN = /^(?:it|this|that|them|him|her|they|yes|no|ok|okay|sure|both|all)$/i;

const tidy = (value: string): string => {
    let out = value.replace(VALUE_END, "");
    for (let i = 0; i < 3; i++) out = out.replace(TRAILING, "");
    return out.replace(/^\s*(?:the|a|an)\s+/i, "").replace(/^["“'\s]+|["”'\s]+$/g, "").trim();
};

/** "Dubai, Abu Dhabi and Qatar" → three places; "Dubai only" → Dubai. */
export const placesIn = (value: string): string[] =>
    tidy(value).split(/\s*(?:,|;|\/|&|\band\b|\bplus\b|\bas\s+well\s+as\b|،)\s*|\s+و(?=[\u0621-\u064A])/i)
        .map((part) => part.replace(/^\s*(?:the|in)\s+/i, "").replace(/\s+only$/i, "").trim())
        .filter((part) => part.length >= 2 && !/^(?:both|all|everywhere|there|here)$/i.test(part));

const ALL_PLACES = /^\s*(?:both|all(?:\s+of\s+them)?|all\s+(?:places|cities|countries)|everywhere|each|the\s+same(?:\s+everywhere)?)\s*\.?\s*$/i;

/** A name worth keeping: 2–60 characters, no pronoun, not a sentence. */
const asName = (value: string | null): string | null => {
    if (!value) return null;
    // the name ends where a phone, an email or a website starts
    const name = tidy(value.split(/\s+(?=(?:\+|00)\d|0\d[\d\s().-]{6,}|[\w.+-]+@|www\.)/)[0]);
    if (name.length < 2 || name.length > 60 || PRONOUN.test(name) || name.split(/\s+/).length > 7) return null;
    return name;
};

/**
 * Read a request or a reply.
 * @param text - What the person typed.
 * @param options.expect - What the last question asked for: a bare reply is that ("ABC" → the name; "both" → the rates' places).
 */
export const parseRecordIntent = (text: string, options: { expect?: "name" | "ratesFor" } = {}): RecordIntent => inEnglish(readIntent(text, options));

/**
 * CRX keeps records in English (2026-10-15): places and an organisation's
 * name given in Arabic are put in English by code; the Arabic name is kept
 * (it goes into the record's notes).
 */
const inEnglish = (intent: RecordIntent): RecordIntent => {
    const place = (value: string) => (hasArabic(value) ? arabicPlaceToEnglish(value) ?? value : value);
    const out: RecordIntent = {
        ...intent,
        base: intent.base ? place(intent.base) : null,
        coverage: intent.coverage.map(place),
        ratesFor: Array.isArray(intent.ratesFor) ? intent.ratesFor.map(place) : intent.ratesFor,
    };
    if (intent.name && hasArabic(intent.name) && intent.kind !== "contact" && intent.kind !== "lead") {
        out.nameArabic = intent.name;
        out.name = arabicCompanyToEnglish(intent.name).english || intent.name;
    }
    return out;
};

const readIntent = (text: string, options: { expect?: "name" | "ratesFor" } = {}): RecordIntent => {
    const raw = westernDigits(text).replace(/\s+/g, " ").trim()
        .replace(/^(?:no|nope|actually|correction|sorry|ok|okay|yes|yeah|right)\b[\s,.:!-]*/i, "")
        .replace(/^(?:لا|كلا|عفوا|عفواً|تصحيح|بل|نعم|تمام|طيب)(?:\s|[،,.:!-])+/, "");
    const intent: RecordIntent = { kind: null, name: null, base: null, coverage: [], ratesFor: null, contact: null, details: "", asksNew: false };
    if (!raw) return intent;
    const kindWord = new RegExp(`\\b(${KINDS})\\b`, "i").exec(raw)?.[1];
    intent.kind = kindWord ? KIND_OF.find(([pattern]) => pattern.test(kindWord))?.[1] ?? null : null;
    // Arabic kind words ("مزود جديد", "عميل", "جهة اتصال", "عميل محتمل")
    if (!intent.kind) {
        intent.kind = /(?:^|\s)عميل\s+محتمل/.test(raw) ? "lead" : /(?:^|\s)(?:مزود|مورد)(?:\s|$)/.test(raw) ? "provider"
            : /(?:^|\s)(?:عميل|زبون)(?:\s|$)/.test(raw) ? "client" : /(?:^|\s)جهة\s+(?:ال)?اتصال/.test(raw) ? "contact" : null;
    }
    if (/(?:^|\s)(?:أضف|اضف|أنشئ|انشئ|سجل|سجّل|اعمل)(?:\s|$)|(?:^|\s)(?:جديد|جديدة)(?:\s|$)/.test(raw)) intent.asksNew = true;
    intent.asksNew = /\b(?:create|add|make|register|set\s+up|start)\b|\bnew\s+(?:provider|supplier|vendor|client|customer|company|contact|lead)\b/i.test(raw);

    // every cue, first one wins where they overlap
    const hits: Array<{ slot: Slot; start: number; end: number }> = [];
    CUES.forEach(({ slot, re }) => {
        re.lastIndex = 0;
        let match: RegExpExecArray | null;
        while ((match = re.exec(raw))) {
            if (slot === "contact" && intent.kind === "contact") break;
            hits.push({ slot, start: match.index, end: match.index + match[0].length });
            if (match[0].length === 0) re.lastIndex += 1;
        }
    });
    hits.sort((a, b) => a.start - b.start || (b.end - b.start) - (a.end - a.start));
    const cues = hits.filter((hit, index) => !hits.slice(0, index).some((other) => hit.start < other.end));
    const valueOf = (index: number) => raw.slice(cues[index].end, cues[index + 1]?.start ?? raw.length);

    cues.forEach((cue, index) => {
        const value = valueOf(index);
        if (cue.slot === "name" && !intent.name) intent.name = asName(value.split(/\s+(?:from|at)\s+(?=[\p{Lu}\p{Lo}])/u)[0]);
        if (cue.slot === "base" && !intent.base) intent.base = tidy(value) || null;
        if (cue.slot === "coverage") intent.coverage.push(...placesIn(value).filter((place) => !intent.coverage.includes(place)));
        if (cue.slot === "ratesFor") intent.ratesFor = ALL_PLACES.test(tidy(value)) ? "all" : placesIn(value);
        if (cue.slot === "contact" && !intent.contact) {
            // the contact keeps its own phones and emails ("with contact Nadia Rahman +966 55 555 0172")
            const end = cues.slice(index + 1).find((next) => next.slot !== "reach")?.start ?? raw.length;
            intent.contact = tidy(raw.slice(cue.end, end)) || null;
        }
    });

    // a bare reply to the last question
    const lead = tidy(raw.slice(0, cues[0]?.start ?? raw.length));
    if (options.expect === "ratesFor" && !cues.length) {
        intent.ratesFor = ALL_PLACES.test(raw) ? "all" : placesIn(raw);
    } else if (!intent.name && lead) {
        // "ABC Security, based in Oman" / "ABC" — a leading name: when a name is what's wanted, when it's all
        // there is, or when a capitalised name comes right before the place words
        const candidate = lead.split(/[,;]|\s+[—–-]\s+/)[0];
        // a question isn't a name ("Is the list for Dubai?")
        const looksLikeName = !/\?\s*$/.test(raw) && /^["“']?[\p{Lu}\p{Lo}\d]/u.test(candidate)
            && !/^(?:please|can|could|would|i|we|they|it|this|that|what|who|how|when|where|why|which|whose|create|add|new|make|show|list|find|tell|is|are|do|does|did|was|were|will|should|has|have|any)\b/i.test(candidate);
        if (options.expect === "name" || (!cues.length && looksLikeName) || (cues.length && looksLikeName)) intent.name = asName(candidate);
    }
    // phones, emails and a website for the record (not the contact's)
    const contactAt = cues.find((cue) => cue.slot === "contact");
    const own = contactAt ? raw.slice(0, contactAt.start) : raw;
    const found = (own.match(/(?:\+|00)\d[\d\s().-]{6,}\d|\b0\d[\d\s().-]{6,}\d\b|[\w.+-]+@[\w-]+(?:\.[\w-]+)+|\bwww\.[\w.-]+\.[a-z]{2,}\b/gi) ?? []).map((part) => part.trim());
    // a local number right after a phone word ("phone 2412 3456"): its country code comes from where it's based
    const digitsOf = (value: string) => value.replace(/\D/g, "");
    cues.forEach((cue, index) => {
        if (cue.slot !== "reach" || (contactAt && cue.start > contactAt.start)) return;
        const number = /^\s*(\d[\d\s().-]{4,}\d)/.exec(valueOf(index))?.[1]?.trim();
        if (number && !found.some((part) => digitsOf(part).endsWith(digitsOf(number)))) found.push(number);
    });
    intent.details = found.join("\n");
    return intent;
};

/** Whether a reply says anything about the record (else it's a new question for Ask). */
export const intentSaysSomething = (intent: RecordIntent): boolean =>
    !!(intent.name || intent.base || intent.coverage.length || intent.ratesFor || intent.contact || intent.details);

/** Later words win; places add up ("also works in Bahrain"). */
export const mergeIntent = (before: RecordIntent, next: RecordIntent): RecordIntent => ({
    kind: next.kind ?? before.kind,
    name: next.name ?? before.name,
    base: next.base ?? before.base,
    coverage: Array.from(new Set([...before.coverage, ...next.coverage])),
    ratesFor: next.ratesFor ?? before.ratesFor,
    contact: next.contact ?? before.contact,
    details: [before.details, next.details].filter(Boolean).join("\n"),
    asksNew: before.asksNew || next.asksNew,
    nameArabic: next.name ? next.nameArabic ?? null : before.nameArabic ?? null,
});
